'use client';

import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import * as React from 'react';
import { Fragment, useMemo, useState } from 'react';
import { HiMagnifyingGlass, HiXMark, HiChevronRight, HiChevronDown } from 'react-icons/hi2';

import { Badge } from './badge';
import { Button } from './button';
import { Input, InputGroup } from './input';

interface TreeNode {
  id: string;
  label: string;
  type: 'parent' | 'child';
  parentId?: string;
  disabled?: boolean;
  data?: any; // Original data object
}

export function TreeMultiSelect<T extends TreeNode>({
  className,
  placeholder,
  autoFocus,
  searchPlaceholder = 'Search...',
  'aria-label': ariaLabel,
  options = [],
  value = [],
  onChange,
  getDisplayValue,
  getSearchValue,
  ...props
}: {
  className?: string;
  placeholder?: React.ReactNode;
  searchPlaceholder?: string;
  autoFocus?: boolean;
  'aria-label'?: string;
  options: T[];
  value?: T[];
  onChange?: (value: T[]) => void;
  getDisplayValue?: (item: T) => string;
  getSearchValue?: (item: T) => string;
} & Omit<Headless.ListboxProps<typeof Fragment, T[]>, 'as' | 'multiple' | 'value' | 'onChange'>) {
  const [searchValue, setSearchValue] = useState('');
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  // Group options into parent-child structure
  const treeStructure = useMemo(() => {
    const parents = options.filter(opt => opt.type === 'parent');
    const children = options.filter(opt => opt.type === 'child');
    
    return parents.map(parent => ({
      parent,
      children: children.filter(child => child.parentId === parent.id)
    }));
  }, [options]);

  // Auto-expand all parents by default to show coverage tags
  React.useEffect(() => {
    if (treeStructure.length > 0) {
      const allParentIds = new Set(treeStructure.map(({ parent }) => parent.id));
      setExpandedParents(allParentIds);
    }
  }, [treeStructure]);

  // Filter tree structure based on search
  const filteredTreeStructure = useMemo(() => {
    if (!searchValue.trim()) return treeStructure;
    
    return treeStructure.map(({ parent, children }) => {
      const parentMatches = (getSearchValue ? getSearchValue(parent) : parent.label)
        .toLowerCase().includes(searchValue.toLowerCase());
      
      const matchingChildren = children.filter(child => 
        (getSearchValue ? getSearchValue(child) : child.label)
          .toLowerCase().includes(searchValue.toLowerCase())
      );
      
      // Include parent if it matches or has matching children
      if (parentMatches || matchingChildren.length > 0) {
        return {
          parent,
          children: parentMatches ? children : matchingChildren
        };
      }
      return null;
    }).filter((item): item is { parent: T; children: T[] } => item !== null);
  }, [treeStructure, searchValue, getSearchValue]);

  // Auto-expand parents that have matching children when searching
  React.useEffect(() => {
    if (searchValue.trim()) {
      const parentsToExpand = new Set<string>();
      filteredTreeStructure.forEach(({ parent, children }) => {
        if (children.length > 0) {
          parentsToExpand.add(parent.id);
        }
      });
      setExpandedParents(parentsToExpand);
    }
  }, [searchValue, filteredTreeStructure]);

  // Get all selectable options (exclude disabled)
  const selectableOptions = useMemo(() => {
    return options.filter(opt => !opt.disabled);
  }, [options]);

  const toggleParentExpansion = (parentId: string) => {
    setExpandedParents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(parentId)) {
        newSet.delete(parentId);
      } else {
        newSet.add(parentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    onChange?.(selectableOptions);
  };

  const handleClearAll = () => {
    onChange?.([]);
  };

  const handleToggleOption = (option: T) => {
    if (option.disabled) return;
    
    const isSelected = value.some(v => v.id === option.id);
    if (isSelected) {
      onChange?.(value.filter(v => v.id !== option.id));
    } else {
      onChange?.([...value, option]);
    }
  };

  // Calculate selection state
  const allSelected = selectableOptions.length > 0 && value.length === selectableOptions.length;
  const noneSelected = value.length === 0;
  const someSelected = !noneSelected && !allSelected;

  return (
    <Headless.Listbox
      as={Fragment}
      multiple
      value={value}
      onChange={onChange || (() => {})}
      {...props}
    >
      <div className={clsx('relative', className)}>
        <Headless.ListboxButton
          autoFocus={autoFocus}
          aria-label={ariaLabel}
          className={clsx([
            // Basic layout
            'group relative block w-full',
            // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
            'before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow',
            // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
            'dark:before:hidden',
            // Hide default focus styles
            'focus:outline-none',
            // Focus ring
            'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent after:data-[focus]:ring-2 after:data-[focus]:ring-secondary-600',
            // Disabled state
            'data-[disabled]:opacity-50 before:data-[disabled]:bg-zinc-950/5 before:data-[disabled]:shadow-none',
          ])}
        >
          <span
            className={clsx([
              // Basic layout
              'relative block w-full appearance-none rounded-lg py-[calc(theme(spacing[2.5])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
              // Set minimum height for when no value is selected
              'min-h-11 sm:min-h-9',
              // Horizontal padding
              'pl-[calc(theme(spacing[3.5])-1px)] pr-[calc(theme(spacing.7)-1px)] sm:pl-[calc(theme(spacing.3)-1px)]',
              // Typography
              'text-left text-base/6 text-zinc-950 placeholder:text-zinc-500 dark:text-white sm:text-sm/6 forced-colors:text-[CanvasText]',
              // Border
              'border border-zinc-950/10 group-data-[active]:border-zinc-950/20 group-data-[hover]:border-zinc-950/20 dark:border-white/10 dark:group-data-[active]:border-white/20 dark:group-data-[hover]:border-white/20',
              // Background color
              'bg-transparent dark:bg-white/5',
              // Invalid state
              'group-data-[invalid]:border-red-500 group-data-[invalid]:group-data-[hover]:border-red-500 group-data-[invalid]:dark:border-red-600 group-data-[invalid]:data-[hover]:dark:border-red-600',
              // Disabled state
              'group-data-[disabled]:border-zinc-950/20 group-data-[disabled]:opacity-100 group-data-[disabled]:dark:border-white/15 group-data-[disabled]:dark:bg-white/[2.5%] dark:data-[hover]:group-data-[disabled]:border-white/15',
              // Placeholder styling
              value.length === 0 ? 'text-zinc-500 dark:text-zinc-400' : 'text-zinc-950 dark:text-white'
            ])}
          >
            {value.length === 0 ? (
              placeholder
            ) : (
              <span className="text-sm">
                {(() => {
                  const parents = value.filter(item => item.type === 'parent').length;
                  const children = value.filter(item => item.type === 'child').length;
                  const parts = [];
                  if (parents > 0) parts.push(`${parents} drug${parents > 1 ? 's' : ''}`);
                  if (children > 0) parts.push(`${children} coverage tag${children > 1 ? 's' : ''}`);
                  return parts.join(', ') || 'Items selected';
                })()}
              </span>
            )}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <svg
              className="size-5 stroke-zinc-500 group-data-[disabled]:stroke-zinc-600 dark:stroke-zinc-400 sm:size-4 forced-colors:stroke-[CanvasText]"
              viewBox="0 0 16 16"
              aria-hidden="true"
              fill="none"
            >
              <path d="M5.75 10.75L8 13L10.25 10.75" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10.25 5.25L8 3L5.75 5.25" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </Headless.ListboxButton>

        <Headless.ListboxOptions
          transition
          anchor="selection start"
          className={clsx(
            // Anchor positioning
            '[--anchor-offset:-1.625rem] [--anchor-padding:theme(spacing.4)] sm:[--anchor-offset:-1.375rem]',
            // Base styles
            'isolate w-max min-w-[calc(var(--button-width)+1.75rem)] select-none scroll-py-1 rounded-xl p-1',
            // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
            'outline outline-1 outline-transparent focus:outline-none',
            // Handle scrolling when menu won't fit in viewport
            'overflow-y-scroll overscroll-contain',
            // Popover background - solid white instead of transparent
            'bg-white dark:bg-zinc-800',
            // Shadows
            'shadow-lg ring-1 ring-zinc-950/10 dark:ring-inset dark:ring-white/10',
            // Transitions
            'transition-opacity duration-100 ease-in data-[transition]:pointer-events-none data-[closed]:data-[leave]:opacity-0',
            // Z-index
            'z-30',
            // Set max width and height
            'max-h-80 max-w-xs'
          )}
        >
          {/* Search Input */}
          <div className="border-b border-zinc-950/10 p-2 dark:border-white/10">
            <div className="relative">
              <HiMagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500 dark:text-zinc-400" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={searchPlaceholder}
                className={clsx([
                  // Layout
                  'block w-full rounded-lg pl-10 pr-8 py-2',
                  // Typography
                  'text-sm text-zinc-950 placeholder:text-zinc-500 dark:text-white dark:placeholder:text-zinc-400',
                  // Border
                  'border border-zinc-950/10 dark:border-white/10',
                  // Background
                  'bg-white dark:bg-zinc-800',
                  // Focus
                  'focus:outline-none focus:ring-2 focus:ring-secondary-600 focus:border-transparent',
                ])}
              />
              {searchValue && (
                <button
                  onClick={() => setSearchValue('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300"
                  title="Clear search"
                  aria-label="Clear search"
                >
                  <HiXMark className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Select All / Clear All / Clear Selection Buttons */}
          {selectableOptions.length > 0 && (
            <div className="border-b border-zinc-950/10 p-2 dark:border-white/10 space-y-2">
              <button
                onClick={noneSelected || someSelected ? handleSelectAll : handleClearAll}
                className="w-full text-center text-sm font-bold border border-zinc-950/10 dark:border-white/10 rounded-lg px-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-700/50 bg-white dark:bg-zinc-800"
              >
                {noneSelected || someSelected ? 'Select All' : 'Clear All'}
              </button>
              {!noneSelected && (
                <button
                  onClick={handleClearAll}
                  className="w-full text-center text-sm font-bold border border-zinc-950/10 dark:border-white/10 rounded-lg px-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-700/50 bg-white dark:bg-zinc-800"
                >
                  Clear Selection
                </button>
              )}
            </div>
          )}

          {/* Tree Options */}
          <div className="max-h-64 overflow-y-auto">
            {filteredTreeStructure.length === 0 ? (
              <div className="px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400">
                No options found
              </div>
            ) : (
              filteredTreeStructure.map(({ parent, children }) => (
                <div key={parent.id}>
                  {/* Parent Option */}
                  <div className={clsx(
                    'group flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2',
                    'hover:bg-zinc-50 dark:hover:bg-zinc-700/50',
                    'data-[focus]:bg-zinc-50 dark:data-[focus]:bg-zinc-700/50',
                    value.some(v => v.id === parent.id) && 'bg-zinc-50 dark:bg-zinc-700/30'
                  )}>
                    {children.length > 0 ? (
                      <button
                        onClick={() => toggleParentExpansion(parent.id)}
                        className="flex items-center text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300"
                        title={expandedParents.has(parent.id) ? 'Collapse' : 'Expand'}
                        aria-label={expandedParents.has(parent.id) ? 'Collapse' : 'Expand'}
                      >
                        {expandedParents.has(parent.id) ? (
                          <HiChevronDown className="h-4 w-4" />
                        ) : (
                          <HiChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    ) : (
                      <div className="w-4 h-4" />
                    )}
                    <div 
                      className="flex flex-1 items-center gap-2"
                      onClick={() => handleToggleOption(parent)}
                    >
                      <input
                        type="checkbox"
                        checked={value.some(v => v.id === parent.id)}
                        onChange={() => {}}
                        className="size-4 rounded border-zinc-300 text-secondary-600 focus:ring-secondary-600 dark:border-zinc-600 dark:text-secondary-600 dark:focus:ring-secondary-600"
                        disabled={parent.disabled}
                        aria-label={`Select ${getDisplayValue ? getDisplayValue(parent) : parent.label}`}
                      />
                      <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                        {getDisplayValue ? getDisplayValue(parent) : parent.label}
                      </span>
                    </div>
                  </div>

                  {/* Child Options */}
                  {expandedParents.has(parent.id) && children.map((child: T) => (
                    <div
                      key={child.id}
                      className={clsx(
                        'group flex cursor-pointer items-center gap-2 rounded-lg py-2 pl-12 pr-2',
                        'hover:bg-zinc-50 dark:hover:bg-zinc-700/50',
                        'data-[focus]:bg-zinc-50 dark:data-[focus]:bg-zinc-700/50',
                        value.some(v => v.id === child.id) && 'bg-zinc-50 dark:bg-zinc-700/30'
                      )}
                      onClick={() => handleToggleOption(child)}
                    >
                      <input
                        type="checkbox"
                        checked={value.some(v => v.id === child.id)}
                        onChange={() => {}}
                        className="size-4 rounded border-zinc-300 text-secondary-600 focus:ring-secondary-600 dark:border-zinc-600 dark:text-secondary-600 dark:focus:ring-secondary-600"
                        disabled={child.disabled}
                        aria-label={`Select ${getDisplayValue ? getDisplayValue(child) : child.label}`}
                      />
                      <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                        {getDisplayValue ? getDisplayValue(child) : child.label}
                      </span>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </Headless.ListboxOptions>
      </div>
    </Headless.Listbox>
  );
}
